<template>
  <BittsDrawer
    :visible="true"
    :title="step === 'form' ? 'Create API Key' : 'API Key Created'"
    :primary-button-text="step === 'form' ? 'Create Key' : 'Done'"
    :secondary-button-text="step === 'form' ? 'Cancel' : ''"
    :is-saving="isCreating"
    :is-saving-disabled="step === 'form' && !canSubmit"
    @action:primary="handleAction"
    @action:secondary="handleClosed"
    @closed="handleClosed"
  >
    <template #content>
      <div v-if="step === 'form'" class="flex flex-col gap-16 p-24">
        <BittsInput
          v-model="keyName"
          name="api-key-name"
          placeholder="e.g. My Integration"
          :form-label="{ title: 'Key Name' }"
          :status="isNameTaken ? 'danger' : 'default'"
          danger-text="A key with that name already exists."
          data-testid="api-key-name-input"
        />

        <BittsAlert
          v-if="!availableScopes.length"
          color="warning"
          message="No API scopes are available for your account. Contact your administrator to create API keys."
        />
        <BittsSelectTags
          v-else
          v-model="selectedScopes"
          mode="multiple"
          :options="scopeOptions"
          :form-label="{ title: 'Scopes' }"
          placeholder="Select scopes"
          data-testid="api-key-scopes-select"
        />

        <ApiKeyCreateDrawerField
          v-slot="{ id }"
          label="Allowed IPs"
          :hint="ipsNote"
          :danger-hint="ipsWarning"
        >
          <BittsFilterTagInput
            :id="id"
            v-model="allowedIps"
            placeholder="Type an IP and press enter. Leave empty to allow any."
          />
        </ApiKeyCreateDrawerField>

        <ApiKeyCreateDrawerField
          v-slot="{ id }"
          label="Expiration Date"
          hint="Optional. Leave blank for no expiration."
        >
          <div class="flex items-center gap-8">
            <BittsRangePicker
              :id="id"
              use-single-date-picker
              :initial-date="expiresDate"
              format="YYYY-MM-DD"
              placeholder="No expiration"
              :disabled-date="disabledExpiryDate"
              @change="onExpiryChange"
            />
            <BittsButton
              v-if="expiresDate"
              type="neutral"
              variant="ghost"
              size="small"
              :center-icon="['fak', 'x']"
              aria-label="Clear expiration date"
              @click="expiresDate = ''"
            />
          </div>
        </ApiKeyCreateDrawerField>
      </div>

      <ApiKeyCreateDrawerResultStep v-else :raw-key="rawKey" />
    </template>
  </BittsDrawer>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsButton,
  BittsDrawer,
  BittsFilterTagInput,
  BittsInput,
  BittsRangePicker,
  BittsSelectTags,
} from '@crossbeam/bitts';

import { computed, ref } from 'vue';

import ApiKeyCreateDrawerField from '@/components/ApiKeyCreateDrawerField.vue';
import ApiKeyCreateDrawerResultStep from '@/components/ApiKeyCreateDrawerResultStep.vue';

import useApiKeyCreateForm from '@/composables/useApiKeyCreateForm';

const { invalidKeyNames = [], availableScopes = [] } = defineProps<{
  invalidKeyNames?: string[];
  availableScopes?: string[];
}>();

const emit = defineEmits<{
  closed: [];
  created: [];
}>();

const step = ref<'form' | 'result'>('form');

const {
  keyName,
  selectedScopes,
  allowedIps,
  expiresDate,
  rawKey,
  isCreating,
  ipsWarning,
  isNameTaken,
  canSubmit,
  disabledExpiryDate,
  onExpiryChange,
  createKey,
} = useApiKeyCreateForm(() => invalidKeyNames);

const scopeOptions = computed(() =>
  availableScopes.map((scope) => ({ value: scope, label: scope })),
);

const ipsNote = computed(() =>
  allowedIps.value.length
    ? ''
    : 'No restriction. Requests accepted from any IP.',
);

async function handleAction() {
  if (step.value === 'result') {
    emit('closed');
    return;
  }
  const createdKeyId = await createKey();
  if (!createdKeyId) return;
  emit('created');
  step.value = 'result';
}

function handleClosed() {
  emit('closed');
}
</script>
